import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { fetchMediaContentTypes, MediaContentType } from '../../../../api/MediaContentTypeApi';
import ModuleContext from '../../_contexts/ModuleContext';
import { getStudentMaterialCount, getTeacherMaterialCount } from '../api/UserMaterialCountApi';
import IdentityContext from '../../../../contexts/IdentityContext';
import { useTranslation } from 'react-i18next';
import {
  getCategoricalContent,
  getStudentVirtualCategories,
  getTeacherVirtualCategories,
} from '../utils/categoricalMaterialUtils';

export type CategoricalContent = {
  name: string;
  contentTypes: MediaContentType[];
};

export type CategoricalContentPerModule = Record<string, CategoricalContent[]>;

export const CategoricalContentTypeContext = createContext<{
  loading: boolean;
  categoricalContentPerModule: CategoricalContentPerModule;
}>({
  loading: true,
  categoricalContentPerModule: {},
});

export const CategoricalContentTypeContextProvider = ({ children }: { children: ReactNode }) => {
  const [categoricalContentPerModule, setCategoricalContentPerModule] = useState<CategoricalContentPerModule>({});
  const [isLoading, setIsLoading] = useState(true);
  const {
    identity: { isTeacherAlike },
  } = useContext(IdentityContext);
  const { t } = useTranslation();

  const { module, subModules } = useContext(ModuleContext);

  const fetchCategoricalContent = useCallback(async () => {
    if (!module) return;
    const modules = [module, ...subModules];
    const moduleIds = modules.map(x => x.id);

    setIsLoading(true);

    const categoricalContentPerModuleMap = await Promise.all(
      moduleIds.map(async moduleId => {
        try {
          const contentTypes = await fetchMediaContentTypes({ moduleId });

          const virtualMaterialCount = isTeacherAlike
            ? await getTeacherMaterialCount(moduleId)
            : await getStudentMaterialCount(moduleId);

          return {
            moduleId,
            content: [
              ...getTeacherVirtualCategories(virtualMaterialCount, t),
              ...getStudentVirtualCategories(virtualMaterialCount, t),
              ...(contentTypes.data.length > 0 ? getCategoricalContent(contentTypes.data) : []),
            ],
          };
        } catch {
          return { moduleId, content: undefined };
        }
      }),
    ).finally(() => setIsLoading(false));

    const categoricalContentPerModule: CategoricalContentPerModule = categoricalContentPerModuleMap.reduce(
      (acc, { moduleId, content }) => {
        if (content) acc[moduleId] = content;
        return acc;
      },
      {} as CategoricalContentPerModule,
    );

    setCategoricalContentPerModule(categoricalContentPerModule);
  }, [isTeacherAlike, module, subModules, t]);

  useEffect(() => {
    fetchCategoricalContent();
  }, [fetchCategoricalContent]);

  return (
    <CategoricalContentTypeContext.Provider value={{ loading: isLoading, categoricalContentPerModule }}>
      {children}
    </CategoricalContentTypeContext.Provider>
  );
};
